import { useMemo } from "react";
import { useAuth } from "../App";

/**
 * InvisibleWatermark — Phase 72 (2026-06-26) universal-coverage rewrite.
 *
 * Renders a fixed, pointer-events-none, full-viewport overlay tiled with
 * the viewing member's EMAIL at ~2.5% effective opacity, rotated -28°.
 * Any screenshot — even a tightly cropped region — contains multiple
 * full email strings when contrast is enhanced.
 *
 * Spec locks (per user Phase 72):
 *   • Content: email only (no name).
 *   • Opacity: 2.5% effective (SVG fill-opacity 0.05 × layer opacity 0.5).
 *   • Exemption: ONLY `ramneetrdh@gmail.com` and `admin@dhdh.ca`.
 *     Every other account — including every admin, moderator, Guest
 *     Professional, trialing/active/paused member — is watermarked.
 *   • Mounted inside AuthenticatedLayout so EVERY route (dashboard,
 *     messages, legal pages, admin pages, future pages) inherits it
 *     automatically without per-page configuration.
 *   • Sized 100vw/100vh so iOS dynamic-viewport quirks never expose an
 *     unwatermarked margin during scroll.
 *   • z-index 40: above page content, below shadcn Dialog overlays.
 */

const WATERMARK_EXEMPT_EMAILS = new Set([
  "ramneetrdh@gmail.com",
  "admin@dhdh.ca",
]);

const InvisibleWatermark = () => {
  const { user } = useAuth();

  const email = useMemo(() => ((user?.email) || "").trim().toLowerCase(), [user]);
  const isExempt = email && WATERMARK_EXEMPT_EMAILS.has(email);

  const dataUri = useMemo(() => {
    if (!email) return null;
    const safe = email
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
    const tileW = 240;
    const tileH = 120;
    // Two anti-aligned instances per tile — makes any rectangular crop
    // contain at least one complete email string.
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${tileW}' height='${tileH}'>
      <g fill='%236B4E8A' fill-opacity='0.05' font-family='Inter, Arial, sans-serif' font-size='12' font-weight='500'>
        <text x='${tileW * 0.25}' y='${tileH * 0.35}' text-anchor='middle' dominant-baseline='middle' transform='rotate(-28 ${tileW * 0.25} ${tileH * 0.35})'>${safe}</text>
        <text x='${tileW * 0.75}' y='${tileH * 0.78}' text-anchor='middle' dominant-baseline='middle' transform='rotate(-28 ${tileW * 0.75} ${tileH * 0.78})'>${safe}</text>
      </g>
    </svg>`.replace(/\n\s*/g, "");
    return `url("data:image/svg+xml;utf8,${svg}")`;
  }, [email]);

  if (!user || isExempt || !dataUri) return null;

  return (
    <div
      data-testid="invisible-watermark"
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 40,
        backgroundImage: dataUri,
        backgroundRepeat: "repeat",
        backgroundPosition: "0 0",
        // 0.05 fill-opacity × 0.5 layer opacity = 2.5% effective.
        opacity: 0.5,
      }}
    />
  );
};

export default InvisibleWatermark;
